import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import FilterDropdown from "./FilterDropdown";
import DataTable from "react-data-table-component";
import Spinner from "../components/ui/Spinner";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Clock,
  Loader2,
  Headset
} from "lucide-react";

// API Imports
import {
  fetchCallDetail,
  fetchPieChartData,
  fetchTopUsersData,
} from "../components/Dashboard/api";
import {
  createPieChartOptions,
  createTopUsersPieChartOptions,
} from "./ChartOptions";
import DashboardStatsView from "./Dashboard/DashboardStats";
import { CallGETAPI } from "../helper/Constants";

/* -----------------------------------------------------------------------
   1. INTERNAL COMPONENT: UnifiedDurationStats
   (Defined here to avoid import/export errors)
   ----------------------------------------------------------------------- */
const formatDuration = (duration) => {
  if (!duration) return '0h 0m 0s';
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const seconds = duration % 60;
  return `${hours}h ${minutes}m ${seconds}s`;
};

const UnifiedDurationStats = ({ startDate, endDate }) => {
  const [stats, setStats] = useState({
    extension_duration: 0,
    callprocessed_duration: 0,
    total_duration: 0,
    tollfree_duration: 0,
    did_duration: 0,
  });
  
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      try {
        const [generalResponse, tollFreeResponse] = await Promise.all([
          CallGETAPI(`api/get-monthly-duration?start_date=${startDate}&end_date=${endDate}`),
          CallGETAPI(`api/get-toll-free-monthly-duration?start_date=${startDate}&end_date=${endDate}`)
        ]);

        let generalTotals = { extension_duration: 0, callprocessed_duration: 0, total_duration: 0 };
        if (generalResponse?.data?.success && generalResponse.data.data.length > 0) {
          generalTotals = generalResponse.data.data.reduce((acc, curr) => ({
            extension_duration: acc.extension_duration + (curr.extension_duration || 0),
            callprocessed_duration: acc.callprocessed_duration + (curr.callprocessed_duration || 0),
            total_duration: acc.total_duration + (curr.total_duration || 0)
          }), generalTotals);
        }

        let tollFreeTotals = { tollfree_duration: 0, did_duration: 0 };
        if (tollFreeResponse?.data?.success && tollFreeResponse.data.data.length > 0) {
          tollFreeTotals = tollFreeResponse.data.data.reduce((acc, curr) => ({
            tollfree_duration: acc.tollfree_duration + (curr.tollfree_duration || 0),
            did_duration: acc.did_duration + (curr.did_duration || 0),
          }), tollFreeTotals);
        }

        setStats({ ...generalTotals, ...tollFreeTotals });

      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [startDate, endDate]);

  const StatCard = ({ label, value, icon: Icon, colorClass, bgClass }) => (
    <div className="flex h-full items-center gap-4 rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm transition-all hover:shadow-md dark:border-zinc-800 dark:bg-black">
      <div className={`p-3 rounded-lg ${bgClass}`}>
        <Icon size={24} className={colorClass} />
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
          {label}
        </p>
        <div className="mt-1">
          {loading ? (
            <div className="flex items-center gap-2 text-zinc-400 dark:text-zinc-500">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : (
            <h4 className="text-lg font-bold text-zinc-900 dark:text-white">
              {formatDuration(value)}
            </h4>
          )}
        </div>
      </div>
    </div>
  );

  const statCardsConfig = [
    { label: "Inbound Duration", value: stats.extension_duration, icon: ArrowDownLeft, bgClass: "bg-zinc-100 dark:bg-zinc-900", colorClass: "text-zinc-900 dark:text-white" },
    { label: "Outbound Duration", value: stats.callprocessed_duration, icon: ArrowUpRight, bgClass: "bg-amber-50 dark:bg-amber-900/20", colorClass: "text-amber-600 dark:text-amber-400" },
    { label: "TollFree Duration", value: stats.tollfree_duration, icon: Headset, bgClass: "bg-zinc-200 dark:bg-zinc-800", colorClass: "text-zinc-800 dark:text-zinc-100" },
    // { label: "DID Duration", value: stats.did_duration, icon: Phone, bgClass: "bg-purple-50 dark:bg-purple-900/20", colorClass: "text-purple-600 dark:text-purple-400" },
    { label: "Total Duration", value: stats.total_duration, icon: Clock, bgClass: "bg-zinc-100 dark:bg-zinc-900", colorClass: "text-zinc-700 dark:text-zinc-300" }
  ];

  return (
    <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {statCardsConfig.map((card, index) => (
        <StatCard key={index} {...card} />
      ))}
    </div>
  );
};

/* -----------------------------------------------------------------------
   2. MAIN DASHBOARD COMPONENT
   ----------------------------------------------------------------------- */
const getChartOptions = (options) => {
  const newOptions = {
    ...options,
    chart: { ...options.chart, backgroundColor: "transparent", style: { fontFamily: "inherit" } },
    title: { ...options.title, style: { color: "var(--chart-text-color, #374151)" } },
    legend: { ...options.legend, itemStyle: { color: "var(--chart-text-color, #374151)" } },
  };

  if (options.xAxis) {
    const xAxes = Array.isArray(options.xAxis) ? [...options.xAxis] : [options.xAxis];
    newOptions.xAxis = xAxes.map((axis) => ({
      ...axis,
      labels: { ...(axis?.labels || {}), style: { color: "var(--chart-text-color, #9ca3af)" } },
    }));
  }

  if (options.yAxis) {
    let yAxes = Array.isArray(options.yAxis) ? [...options.yAxis] : [options.yAxis];
    const requiresSecondAxis = options.series && options.series.some((s) => s.yAxis === 1);
    if (requiresSecondAxis && yAxes.length < 2) {
      yAxes.push({ title: { text: "" }, labels: { enabled: false }, visible: false });
    }
    newOptions.yAxis = yAxes.map((axis) => ({
      ...axis,
      labels: { ...(axis?.labels || {}), style: { color: "var(--chart-text-color, #9ca3af)" } },
      title: { ...(axis?.title || {}), style: { color: "var(--chart-text-color, #374151)" } },
    }));
  }
  return newOptions;
};

function Dashboard({ embedded = false }) {
  const [pieChartData, setPieChartData] = useState([]);
  const [topUsersData, setTopUsersData] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [filteredStats, setFilteredStats] = useState({ totalCalls: 0, answeredCalls: 0, cancelCalls: 0, missCalls: 0 });
  const [transactionData, setTransactionData] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  useEffect(() => {
    const currentDate = new Date();
    const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    setStartDate(startOfMonth.toISOString().split("T")[0]);
    setEndDate(currentDate.toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) loadDashboardData(startDate, endDate);
  }, [startDate, endDate]);

  const loadDashboardData = async (start, end) => {
    setLoading(true);
    try {
      const [callDetailResponse, pieChartDataResponse, topUsersDataResponse] = await Promise.all([
        fetchCallDetail(start, end),
        fetchPieChartData(start, end),
        fetchTopUsersData(start, end),
      ]);

      if (callDetailResponse) {
        const { totals } = callDetailResponse;
        if (totals) {
          setFilteredStats(totals);
        }
      }
      setPieChartData(pieChartDataResponse);
      setTopUsersData(topUsersDataResponse);
    } catch (error) {
      console.error("Error loading dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoadingTransactions(true);
      try {
        const response = await CallGETAPI("api/admin/balance-transactions");
        const filteredData = (response?.data?.data || [])
          .filter((item) => item.transaction_type === "Credit" && item.status === "completed" && item.description === "Balance Updated Successfully")
          .map(({ date, user_name, amount, user_id, user_email }) => ({ date, user_name, amount, user_id, user_email }));
        setTransactionData(filteredData);
      } catch (error) {
        console.error("Error fetching transactions:", error);
      } finally {
        setLoadingTransactions(false);
      }
    };
    fetchTransactions();
  }, []);

  const handleFilterChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const customStyles = {
    table: { style: { backgroundColor: "transparent" } },
    headRow: { style: { backgroundColor: "var(--table-header-bg, #f3f4f6)", borderBottomWidth: "1px", borderColor: "var(--table-border-color, #e5e7eb)", minHeight: "52px" } },
    headCells: { style: { fontSize: "0.875rem", fontWeight: "600", color: "var(--table-text-color, #374151)", textTransform: "uppercase", letterSpacing: "0.05em", paddingLeft: "16px", paddingRight: "16px" } },
    rows: { style: { fontSize: "0.875rem", backgroundColor: "transparent", color: "var(--table-text-color, #374151)", minHeight: "60px", "&:not(:last-of-type)": { borderBottomStyle: "solid", borderBottomWidth: "1px", borderBottomColor: "var(--table-border-color, #e5e7eb)" }, "&:hover": { backgroundColor: "var(--table-row-hover, rgba(0,0,0,0.02))" } } },
    pagination: { style: { backgroundColor: "transparent", color: "var(--table-text-color, #374151)", fontSize: "0.875rem" } },
  };

  const columns = [
    { name: "Date", selector: (row) => row.date, grow: 1 },
    { name: "User Id", selector: (row) => row.user_id, grow: 1 },
    { name: "User Email", selector: (row) => row.user_email, grow: 2 },
    { name: "Account", selector: (row) => row.user_name, grow: 1 },
    { name: "Amount", selector: (row) => row.amount, width: "120px", cell: (row) => (<span className="font-semibold text-zinc-900 dark:text-zinc-100">${row.amount}</span>) },
  ];

  return (
    <section
      className={`w-full max-w-full min-w-0 overflow-x-hidden text-zinc-900 transition-colors duration-300 dark:text-zinc-100 ${
        embedded ? "" : "min-h-screen bg-zinc-50 dark:bg-black"
      }`}
    >
      <style>{`:root{--chart-text-color:#18181b;--table-text-color:#18181b;--table-header-bg:#f4f4f5;--table-border-color:#e4e4e7;--table-row-hover:rgba(24,24,27,0.04);--chart-bg:#ffffff}.dark{--chart-text-color:#f4f4f5;--table-text-color:#f4f4f5;--table-header-bg:#18181b;--table-border-color:#27272a;--table-row-hover:rgba(255,255,255,0.06);--chart-bg:#000000}`}</style>

      <div className="mb-6 flex flex-wrap justify-end gap-4">
        <div className="w-full max-w-full md:w-auto z-10">
          <FilterDropdown onFilterChange={handleFilterChange} loading={loading} />
        </div>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="min-w-0 space-y-6 xl:col-span-12">
          <div className="w-full min-w-0">
            <UnifiedDurationStats startDate={startDate} endDate={endDate} />
          </div>
          <div className="w-full min-w-0">
            <DashboardStatsView stats={filteredStats} />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20 xl:col-span-12"><Spinner animation="border" className="w-10 h-10 text-zinc-900 dark:text-white" /></div>
        ) : (
          <>
            <div className="min-w-0 xl:col-span-6">
              <div className="h-full overflow-hidden rounded-3xl border border-zinc-200 bg-white p-4 shadow-sm transition-colors sm:p-6 dark:border-zinc-800 dark:bg-black">
                <h5 className="mb-4 text-lg font-semibold text-zinc-900 dark:text-white">Extension Call</h5>
                {pieChartData.length > 0 ? <div className="min-w-0 overflow-hidden"><HighchartsReact highcharts={Highcharts} options={getChartOptions(createPieChartOptions(pieChartData))} /></div> : <div className="flex h-64 items-center justify-center text-zinc-400 dark:text-zinc-500">No Data Found</div>}
              </div>
            </div>
            <div className="min-w-0 xl:col-span-6">
              <div className="h-full overflow-hidden rounded-3xl border border-zinc-200 bg-white p-4 shadow-sm transition-colors sm:p-6 dark:border-zinc-800 dark:bg-black">
                <h5 className="mb-4 text-lg font-semibold text-zinc-900 dark:text-white">Top Users by Duration</h5>
                <div className="min-w-0 overflow-hidden">
                  <HighchartsReact highcharts={Highcharts} options={getChartOptions(createTopUsersPieChartOptions(topUsersData))} />
                </div>
              </div>
            </div>
          </>
        )}

        <div className="min-w-0 xl:col-span-12">
          <div className="rounded-3xl border border-zinc-200 bg-white p-4 shadow-sm transition-colors sm:p-6 dark:border-zinc-800 dark:bg-black">
            <h2 className="mb-6 text-xl font-bold text-zinc-900 dark:text-white">Refill History</h2>
            {loadingTransactions ? <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-zinc-900 dark:border-white"></div></div> : <div className="w-full max-w-full overflow-x-auto rounded-2xl border border-zinc-200 dark:border-zinc-800"><DataTable columns={columns} data={transactionData} pagination responsive customStyles={customStyles} className="dark:text-zinc-300" /></div>}
          </div>
        </div>
      </div>
    </section>
  );
}

export default Dashboard;
